/**
 * Storage adapter.
 *
 * The app was originally built as a Claude artifact, where a global
 * `window.storage` object is injected by the host. That API does not exist in a
 * browser, so every save/load silently fails once the app is deployed. This
 * module reimplements the same surface on top of localStorage and installs it as
 * `window.storage` when the host hasn't provided one.
 *
 * Installing a shim (rather than rewriting call sites) means the ~13 existing
 * `window.storage.*` calls in the app keep working untouched, so the migration
 * can't introduce behavioural drift. New code should import { storage } directly.
 *
 * API mirrors the artifact host exactly:
 *   get(key)            -> { key, value, shared } | null
 *   set(key, value)     -> { key, value, shared } | null
 *   delete(key)         -> { key, deleted, shared } | null
 *   list(prefix)        -> { keys, prefix, shared } | null
 *
 * Values are strings. Callers JSON.stringify / JSON.parse themselves, as before.
 */

const NAMESPACE = "yaadgrow:";

/** In-memory fallback so the app still works in private mode / storage-disabled. */
const memoryStore = new Map();
let usingMemoryFallback = false;

function resolveBackend() {
  try {
    const probe = "__yaadgrow_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    // Safari private mode, disabled cookies, embedded webviews, etc.
    usingMemoryFallback = true;
    return null;
  }
}

const backend = typeof window !== "undefined" ? resolveBackend() : null;

const nsKey = (key) => `${NAMESPACE}${key}`;
const stripNs = (key) => key.slice(NAMESPACE.length);

function readRaw(key) {
  if (backend) return backend.getItem(nsKey(key));
  return memoryStore.has(nsKey(key)) ? memoryStore.get(nsKey(key)) : null;
}

function writeRaw(key, value) {
  if (backend) {
    backend.setItem(nsKey(key), value);
    return;
  }
  memoryStore.set(nsKey(key), value);
}

function removeRaw(key) {
  if (backend) {
    backend.removeItem(nsKey(key));
    return;
  }
  memoryStore.delete(nsKey(key));
}

function allKeys() {
  if (backend) {
    const out = [];
    for (let i = 0; i < backend.length; i++) {
      const k = backend.key(i);
      if (k && k.startsWith(NAMESPACE)) out.push(stripNs(k));
    }
    return out;
  }
  return [...memoryStore.keys()].map(stripNs);
}

/** Thrown-quota detection so callers can surface a useful message. */
export class StorageQuotaError extends Error {
  constructor(message) {
    super(message);
    this.name = "StorageQuotaError";
  }
}

export const storage = {
  async get(key, shared = false) {
    const value = readRaw(key);
    if (value === null) return null;
    return { key, value, shared };
  },

  async set(key, value, shared = false) {
    const asString = typeof value === "string" ? value : String(value);
    try {
      writeRaw(key, asString);
    } catch (err) {
      // QuotaExceededError (22 / 1014) — surface a typed error rather than
      // failing silently, so the app's existing try/catch can report it.
      throw new StorageQuotaError(
        "Device storage is full. Export a backup and remove old logs to free space."
      );
    }
    return { key, value: asString, shared };
  },

  async delete(key, shared = false) {
    const existed = readRaw(key) !== null;
    removeRaw(key);
    return { key, deleted: existed, shared };
  },

  async list(prefix = "", shared = false) {
    const keys = allKeys().filter((k) => k.startsWith(prefix));
    return { keys, prefix, shared };
  },
};

/** True when persistence is in-memory only (data will not survive a reload). */
export function isEphemeral() {
  return usingMemoryFallback;
}

/**
 * Install as window.storage when the host hasn't supplied one.
 * Call once, before the app mounts.
 */
export function installStorageShim() {
  if (typeof window === "undefined") return;
  if (window.storage && typeof window.storage.get === "function") return; // host provided
  window.storage = storage;
}
